// apps/client/src/pages/auth/NativeEnroll2FAPage.tsx
//
// Route page for /enroll-2fa — the native GCIP TOTP enrolment surface for an
// already-signed-in user who must enrol (enforcement migration). Renders inside
// AppLayout, so it centres the shared MfaEnrollmentCard in the content area
// (no min-h-screen per apps/client/CLAUDE.md).
//
// The roleless-bootstrap case does NOT use this page (a claim-less session never
// clears ProtectedRoute). That path renders <MfaEnrollmentCard user={...}/>
// directly inside the invitation flow, before claims are granted — see the
// component's docblock and the handover note.

import React, { useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import MfaEnrollmentCard from '../../components/auth/MfaEnrollmentCard';
import { hasEnrolledTotp } from '../../lib/nativeMfa';
import Loading from '../../components/common/Loading';

const NativeEnroll2FAPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleEnrolled = useCallback(async (): Promise<void> => {
    // Refresh the ID token so downstream guards observe the enrolled factor,
    // then return to the dashboard.
    try {
      await getAuth().currentUser?.getIdToken(true);
    } catch {
      // Non-fatal — navigation still proceeds; the next guarded read refreshes.
    }
    navigate('/', { replace: true });
  }, [navigate]);

  if (!user) {
    // Should not happen inside ProtectedRoute, but guard rather than crash.
    return <Loading />;
  }

  // GCIP allows only one TOTP factor per account; an already-enrolled user has
  // nothing to do here (there is no "add another authenticator"). Send them home
  // rather than into a maximum-second-factor-count-exceeded dead-end.
  if (hasEnrolledTotp(user)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex items-center justify-center min-h-[70vh] px-4">
      <MfaEnrollmentCard user={user} onEnrolled={handleEnrolled} onReauth={() => void logout()} />
    </div>
  );
};

export default NativeEnroll2FAPage;
